import { Link } from 'react-router-dom'
import { ExternalLink, ListPlus, Mail, Send } from 'lucide-react'
import Card from '@/components/layout/Card'
import Select from '@/components/ui/Select'
import { useLists } from '@/features/lists/useLists'
import { useTemplates } from '@/features/messaging/useMessaging'
import { useDripSequences } from '@/features/drip/useDrip'

export interface AfterSubmitValue {
  addToListId: string
  autoReplyTemplateId: string
  enrolSequenceId: string
}

function Step({ icon, title, hint, children }: { icon: React.ReactNode; title: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-brand-darkest">{title}</div>
        <p className="text-xs text-gray-500 mb-1.5">{hint}</p>
        {children}
      </div>
    </div>
  )
}

/**
 * What happens to the person who submitted, as part of the form itself — add them to a list, email
 * them straight away, start a sequence — so a campaign doesn't need a separate list and workflow.
 */
export default function AfterSubmitSteps({ value, onChange }: { value: AfterSubmitValue; onChange: (v: AfterSubmitValue) => void }) {
  const { data: lists } = useLists()
  const { data: templates } = useTemplates('email')
  const { data: sequences } = useDripSequences()
  const manual = (lists ?? []).filter((l) => l.listType === 'manual')
  const template = (templates ?? []).find((t) => t.id === value.autoReplyTemplateId)
  const sequence = (sequences ?? []).find((s) => s.id === value.enrolSequenceId)
  const set = (patch: Partial<AfterSubmitValue>) => onChange({ ...value, ...patch })

  return (
    <Card title="Then, for the person who submitted">
      <div className="space-y-5">
        <Step icon={<ListPlus size={16} />} title="Add them to a list" hint="Everyone who fills in this form joins the list — ready to email or start a sequence later.">
          <Select size="md" value={value.addToListId} onChange={(e) => set({ addToListId: e.target.value })} className="w-full" aria-label="Add them to a list">
            <option value="">Don’t add to a list</option>
            {manual.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
          {manual.length === 0 && (
            <p className="text-xs text-gray-400 mt-1">No manual lists yet — <Link to="/lists" className="text-brand-primary hover:underline">create one on Lists</Link>.</p>
          )}
        </Step>

        <Step icon={<Mail size={16} />} title="Email them straight away" hint="Sent from the lead’s owner as soon as they submit — e.g. the property details they asked for.">
          <Select size="md" value={value.autoReplyTemplateId} onChange={(e) => set({ autoReplyTemplateId: e.target.value })} className="w-full" aria-label="Email them straight away">
            <option value="">Don’t send an email</option>
            {(templates ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
          {template && (
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
              Subject: <span className="font-medium text-gray-700 truncate">{template.subject}</span>
              <Link to="/settings?tab=templates" className="ml-auto shrink-0 inline-flex items-center gap-1 text-brand-primary hover:underline">Edit template <ExternalLink size={11} /></Link>
            </p>
          )}
        </Step>

        <Step icon={<Send size={16} />} title="Start an email sequence" hint="They get the sequence’s emails on its schedule. Anyone already in it isn’t added twice.">
          <Select size="md" value={value.enrolSequenceId} onChange={(e) => set({ enrolSequenceId: e.target.value })} className="w-full" aria-label="Start an email sequence">
            <option value="">Don’t start a sequence</option>
            {(sequences ?? []).map((s) => (
              <option key={s.id} value={s.id} disabled={s.steps.length === 0}>
                {s.name}{!s.enabled ? ' (paused)' : ''}{s.steps.length === 0 ? ' (no emails yet)' : ''}
              </option>
            ))}
          </Select>
          {sequence && !sequence.enabled && (
            <p className="text-xs text-amber-700 mt-1">This sequence is paused — nobody is enrolled until it’s live.</p>
          )}
        </Step>
      </div>
    </Card>
  )
}
